import { Chip, Stack, Tab, Tabs } from "@mui/material";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";

import type { ReactNode } from "react";

import { useNotificationRejects } from "@/api/zero/notificationRejects";
import { useAdminNotificationsTotal } from "@/api/zero/notificationsAdmin";
import { SettingsPageLayout } from "@/features/Settings/SettingsPageLayout";
import {
  REJECTS_FETCH_PAGE_SIZE,
  RejectsTab,
} from "@/features/ZeroAdmin/notificationRejects/RejectsTab";
import { AllNotificationsTab } from "@/features/ZeroAdmin/notifications/AllNotificationsTab";
import {
  NOTIFICATIONS_TABS,
  notificationsSearchSchema,
  isOneOf,
  type NotificationsTab,
} from "@/features/ZeroAdmin/notifications/filters";

export const Route = createFileRoute(
  "/_auth/settings/$siteId/_zeroAdmin/notifications/",
)({
  validateSearch: zodValidator(notificationsSearchSchema),
  component: NotificationsAdminPage,
});

function NotificationsAdminPage() {
  const { tab = "all", page, ...filters } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const { data: storedTotal } = useAdminNotificationsTotal();
  const { data: rejectsPage } = useNotificationRejects({
    pageSize: REJECTS_FETCH_PAGE_SIZE,
  });
  const rejectsCount = rejectsPage
    ? `${rejectsPage.rejects.length}${rejectsPage.next_page_token ? "+" : ""}`
    : undefined;

  const switchTab = (next: NotificationsTab) =>
    void navigate({ search: next === "all" ? {} : { tab: next } });

  return (
    <SettingsPageLayout
      breadcrumbs={[{ label: "Settings" }]}
      title="Notifications"
      description="Everything the notification service has stored, across every site and user. Search, inspect, and delete what should never have been sent. Deleting removes a notification from every staff user's bell."
      data-testid="notifications-admin-page"
    >
      <Stack sx={{ gap: 4 }}>
        <Tabs
          value={tab}
          onChange={(_event, next: unknown) => {
            if (isOneOf(NOTIFICATIONS_TABS, next)) switchTab(next);
          }}
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab
            value="all"
            label={<TabLabel label="All notifications" count={storedTotal} />}
            data-testid="notifications-admin-tab-all"
          />
          <Tab
            value="rejects"
            label={<TabLabel label="Rejects" count={rejectsCount} />}
            data-testid="notifications-admin-tab-rejects"
          />
        </Tabs>

        {tab === "rejects" ? (
          <RejectsTab
            search={{ producer: filters.producer, page }}
            onSearchChange={(patch, options) =>
              void navigate({
                search: (prev) => ({ ...prev, ...patch }),
                replace: options?.replace,
              })
            }
          />
        ) : (
          <AllNotificationsTab
            search={filters}
            onSearchChange={(patch) =>
              void navigate({
                search: (prev) => ({ ...prev, ...patch, page: undefined }),
              })
            }
          />
        )}
      </Stack>
    </SettingsPageLayout>
  );
}

function TabLabel({
  label,
  count,
}: {
  label: ReactNode;
  count: number | string | undefined;
}) {
  return (
    <Stack direction="row" sx={{ alignItems: "center", gap: 2 }}>
      {label}
      {count !== undefined && <Chip size="small" label={count} />}
    </Stack>
  );
}
